import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, Loader2, MailCheck, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { gotoApp } from "@/lib/appUrl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Logo } from "@/components/Logo";
import { GoogleButton } from "@/components/GoogleButton";

const field =
  "h-11 bg-zinc-900/60 border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-indigo-500/40";

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", org_name: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  const [resent, setResent] = useState("");
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    api.get("/auth/providers").then((r) => setGoogleEnabled(!!r.data.google)).catch(() => setGoogleEnabled(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      sessionStorage.setItem("pj_post_auth", "/plans");
      const data = await register(form);
      if (data.verification_required) {
        sessionStorage.removeItem("pj_post_auth");
        setPending(data);
        return;
      }
      if (await gotoApp(data.access_token, "/plans")) return;
      navigate("/plans");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setResent("sending");
    try {
      await api.post("/auth/resend-verification-public", { email: form.email });
      setResent("sent");
    } catch (e) {
      setResent(apiError(e));
    }
  };

  return (
    <div className="min-h-screen nv-grid-bg flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-[440px] nv-rise">
        <div className="flex items-center justify-between gap-3 mb-10">
          <div className="flex items-center gap-2.5">
            <Logo size={30} withWordmark className="text-lg" testid="auth-logo" />
          </div>
          <ThemeToggle />
        </div>

        {pending ? (
          <div data-testid="signup-verify-pending" className="space-y-5">
            <div className="w-11 h-11 rounded-xl bg-indigo-500/15 border border-indigo-500/30 grid place-items-center">
              <MailCheck className="w-5 h-5 text-indigo-400" />
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight">Confirm your email</h1>
            <p className="text-sm text-zinc-400 leading-relaxed">
              Your workspace is created. We sent a confirmation link to{" "}
              <strong className="text-zinc-200 font-medium">{pending.email}</strong>. Click it to activate your
              account — you can sign in right after.
            </p>
            {!pending.verification_sent && (
              <p data-testid="signup-verify-send-failed" className="text-sm text-amber-300 bg-amber-500/10 border border-amber-500/25 rounded-lg px-3 py-2 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                We could not deliver that email just now. Try resending in a moment.
              </p>
            )}
            <div className="flex flex-wrap items-center gap-3">
              <Button
                data-testid="signup-resend-verification"
                onClick={resend}
                disabled={resent === "sending" || resent === "sent"}
                className="h-10 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg"
              >
                {resent === "sent" ? "Email sent" : resent === "sending" ? "Sending…" : "Resend email"}
              </Button>
              <Link data-testid="signup-goto-login" to="/login" className="text-sm text-indigo-400 hover:text-indigo-300">
                Go to sign in
              </Link>
            </div>
            {resent && resent !== "sending" && resent !== "sent" && <p className="text-xs text-red-300">{resent}</p>}
          </div>
        ) : (
          <>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Create your workspace</h1>
            <p className="text-sm text-zinc-500 mt-2 mb-8">You'll be the owner. Invite your team after setup.</p>

            <form onSubmit={submit} className="space-y-3">
              {googleEnabled && (
                <>
                  <GoogleButton label="Sign up with Google" testid="google-signup-btn" onError={setError} />
                  <div className="flex items-center gap-3 py-1">
                    <span className="h-px flex-1 bg-zinc-800" />
                    <span className="text-[10px] font-num uppercase tracking-[0.18em] text-zinc-600">or with email</span>
                    <span className="h-px flex-1 bg-zinc-800" />
                  </div>
                </>
              )}

              <Input data-testid="signup-org-input" className={field} required placeholder="Company / agency name" value={form.org_name} onChange={set("org_name")} />
              <Input data-testid="signup-name-input" className={field} required placeholder="Your full name" value={form.name} onChange={set("name")} />
              <Input data-testid="signup-email-input" className={field} type="email" required placeholder="you@company.com" value={form.email} onChange={set("email")} />
              <Input data-testid="signup-password-input" className={field} type="password" required minLength={8} placeholder="Password (min 8 characters)" value={form.password} onChange={set("password")} />
              {error && (
                <p data-testid="signup-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}
              <Button data-testid="signup-submit-btn" type="submit" disabled={busy} className="w-full h-11 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Create workspace <ArrowRight className="w-4 h-4 ml-1.5" /></>}
              </Button>
            </form>

            <p className="text-sm text-zinc-500 mt-6">
              Already have an account?{" "}
              <Link data-testid="goto-login-link" to="/login" className="text-indigo-400 hover:text-indigo-300">
                Sign in
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
